import React, { useEffect, useState } from 'react';
import Container from '../components/Container';
import PageHeader from '../components/PageHeader';
import ServiceStatusTable from '../components/ServiceStatusTable';
import StatsCard from '../components/StatsCard';
import useApiCaller from '../hooks/use-api-caller';
import { ServiceStatus, Status, IMetricSeries, IPipelineStatus, ILoginStatus, IPipelineStatusResponse } from '../types/dashboard';
import MetricGraph from '../components/MetricGraph';
import Loading from '../components/Loading';
import PipelineStatusTable from '../components/PipelineStatusTable';
import { getDatabricksJobIds } from '../utils/config';

const initialServiceStatus: ServiceStatus[] = [
  { name: 'Frontend', status: 'unknown' },
  { name: 'Backend API', status: 'unknown' },
  { name: 'Database', status: 'unknown' },
];

const fetchStatus = async (service: string, apiCaller: ReturnType<typeof useApiCaller>): Promise<Status> => {
  try {
    if (service === 'Frontend') return 'healthy';
    if (service === 'Backend API') {
      const data = await apiCaller.health();
      if (data.status) {
        return data.status === 'Healthy!' ? 'healthy' : 'unhealthy';
      }
      return 'unknown';
    }
    if (service === 'Database') {
      const response = await apiCaller.healthDb();
      if (response.healthy) {
        return response.healthy ? 'healthy' : 'unhealthy';
      }
      return 'unknown';
    }
    return 'unknown';
  } catch (e) {
    return 'unknown';
  }
};

export default function Dashboard() {
  const apiCaller = useApiCaller();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ServiceStatus[]>(initialServiceStatus);
  const [metricSeries, setMetricSeries] = useState<IMetricSeries[]>([]);
  const [pipelineStatus, setPipelineStatus] = useState<IPipelineStatus[]>([]);
  const [loginStats, setLoginStats] = useState<ILoginStatus>({ success: 0, unauthorised: 0, failure: 0 });

  const updateStatus = async () => {
    const updatedStatus = await Promise.all(
      status.map(async (service) => {
        const status = await fetchStatus(service.name, apiCaller);
        return {
          ...service,
          status,
          lastChecked: new Date().toString(),
        };
      })
    );
    setStatus(updatedStatus);
  };

  const updateMetrics = async () => {
    try {
      const data: IMetricSeries[] = await apiCaller.getMetrics();
      const roundedDataToSeconds = data.map((series) => ({
        ...series,
        data: series.data.map((point) => ({
          ...point,
          timestamp: (() => {
            const d = new Date(point.timestamp);
            d.setMilliseconds(0);
            return d.toISOString();
          })(),
        })),
      }));
      setMetricSeries(roundedDataToSeconds);
    } catch (e) {
      setMetricSeries([]);
    }
  };

  const updatePipelineStatus = async () => {
    const job_ids = getDatabricksJobIds();
    const pipelineRunsResp: IPipelineStatusResponse = await apiCaller.getPipelineStatus(Object.keys(job_ids).join(','));
    const pipelineRuns = Object.entries(job_ids).map(([job_id, info]) => ({
      pipeline_name: info.name || job_id,
      description: info.description || '',
      job_id,
      runs: pipelineRunsResp[job_id] || [],
    }));
    setPipelineStatus(pipelineRuns);
  };

  const updateLoginStats = async () => {
    try {
      const statsResp = await apiCaller.getLoginAttempts();
      const stats = {
        success: statsResp.filter((r) => r.status_code === '200').length,
        unauthorised: statsResp.filter((r) => r.status_code === '401').length,
        failure: statsResp.filter((r) => r.status_code === '500').length,
      };
      setLoginStats(stats);
    } catch {
      setLoginStats({ success: 0, unauthorised: 0, failure: 0 });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.allSettled([updateStatus(), updateMetrics(), updatePipelineStatus(), updateLoginStats()]);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const serviceColours: Record<string, string> = {
    Frontend: '#1f77b4', // blue
    'Backend API': '#ff7f0e', // orange
    Database: '#2ca02c', // green
  };

  return (
    <div className="mb-auto bg-white lg:bg-ow-lighter-grey">
      <PageHeader pageSubtitle="System Health Dashboard" lastUpdated={new Date().toString()} />
      <Container>
        <div className="lg:flex lg:justify-center lg:w-full">
          <div className="lg:w-full lg:bg-white lg:p-8">
            {loading && <Loading />}
            {!loading && (
              <div>
                <div className="flex flex-row gap-8 justify-center items-center">
                  <div className="flex-1">
                    <ServiceStatusTable data={status} />
                  </div>
                  <div className="flex-shrink-0 flex justify-end">
                    <StatsCard
                      title="Login Attempts Today"
                      stats={[
                        { label: 'Successful (200):', value: loginStats.success, colorClass: 'text-green-600' },
                        { label: 'Unauthorised (401):', value: loginStats.unauthorised, colorClass: 'text-orange-600' },
                        { label: 'Failure (500):', value: loginStats.failure, colorClass: 'text-red-600' },
                      ]}
                    />
                  </div>
                </div>
                <div className="mt-8">
                  <MetricGraph title="CPU Usage" metricType="cpu" metricSeries={metricSeries} serviceColours={serviceColours} />
                </div>
                <div className="mt-8">
                  <MetricGraph title="Memory Utilisation" metricType="memory" metricSeries={metricSeries} serviceColours={serviceColours} />
                </div>
                <div className="mt-8">
                  <MetricGraph
                    title="Database Connections"
                    metricType="connections"
                    metricSeries={metricSeries}
                    serviceColours={serviceColours}
                    maxDbConnections={2760}
                  />
                </div>

                <PipelineStatusTable data={pipelineStatus} />
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}
